import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";

const portalDest = (role) =>
  ["admin", "staff"].includes(role) ? "/portal/admin" : "/portal/members";

export default function Auth() {
  const { login, user, loaded, refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [forceChange, setForceChange] = useState(false);
  const [newPass, setNewPass]         = useState("");
  const [confirmPass, setConfirmPass] = useState("");

  // If already logged in: force-change screen or redirect
  useEffect(() => {
    if (!loaded || !user) return;
    if (user.must_change_password) {
      setForceChange(true);
    } else {
      navigate(portalDest(user.role), { replace: true });
    }
  }, [loaded, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.must_change_password) {
        setForceChange(true);
        toast.info("Please set a new password to continue");
      } else {
        toast.success(`Welcome back, ${u.name}!`);
        navigate(portalDest(u.role), { replace: true });
      }
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (newPass !== confirmPass) { toast.error("Passwords don't match"); return; }
    if (newPass.length < 4) { toast.error("Password must be at least 4 characters"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { new_password: newPass });
      await refresh();
      toast.success("Password set! Welcome.");
      navigate(portalDest(user?.role), { replace: true });
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (forceChange) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary/5 px-4">
        <div className="w-full max-w-md">
          <Brand size="md" className="mb-8" />
          <form onSubmit={submitNewPassword} className="rounded-3xl bg-white border border-border p-8 shadow-xl shadow-primary/10">
            <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mx-auto mb-4">
              <KeyRound size={28} className="text-primary" />
            </div>
            <h1 className="font-display text-3xl text-center text-slate-800">Set Your Password</h1>
            <p className="text-center text-slate-500 text-sm mt-1 mb-6">
              For your security, please choose a new password before continuing.
            </p>
            <div className="space-y-4">
              <div>
                <Label>New Password</Label>
                <Input data-testid="new-password" type="password" className="rounded-xl mt-1"
                  value={newPass} onChange={(e) => setNewPass(e.target.value)} required autoFocus />
              </div>
              <div>
                <Label>Confirm Password</Label>
                <Input data-testid="confirm-password" type="password" className="rounded-xl mt-1"
                  value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} required />
              </div>
              <Button type="submit" disabled={loading} data-testid="change-password-submit"
                className="w-full rounded-full font-display text-lg bg-primary hover:bg-primary/90 py-6">
                {loading ? <Loader2 className="animate-spin" /> : "Set Password & Continue"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary/5 px-4">
      <div className="w-full max-w-md">
        <Brand size="md" className="mb-8" />
        <form onSubmit={submit} className="rounded-3xl bg-white border border-border p-8 shadow-xl shadow-primary/10">
          <h1 className="font-display text-3xl text-center text-slate-800">Welcome Back</h1>
          <p className="text-center text-slate-500 text-sm mt-1 mb-6">Log in to your Fun-Nastix portal</p>
          <div className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input data-testid="login-email" type="email" className="rounded-xl mt-1"
                value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="login-password" type="password" className="rounded-xl mt-1"
                value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full rounded-full font-display text-lg bg-primary hover:bg-primary/90 py-6">
              {loading ? <Loader2 className="animate-spin" /> : "Log In"}
            </Button>
          </div>
          <p className="text-center text-sm text-slate-500 mt-6">
            New to Fun-Nastix?{" "}
            <button type="button" onClick={() => navigate("/book-online")}
              className="text-primary font-semibold" data-testid="login-to-book">
              Book a class
            </button>{" "}
            to create your account.
          </p>
        </form>
      </div>
    </div>
  );
}
